import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { focusRing } from '../lib/focus-ring';
import { Spinner } from './Spinner';

/**
 * Exported so a router link can be styled as a button without the component
 * needing to know about routing:
 *
 *     <Link to="/tenants/new" className={buttonVariants({ variant: 'secondary' })}>
 */
export const buttonVariants = cva(
  cn(
    'inline-flex shrink-0 cursor-pointer select-none items-center justify-center gap-2 whitespace-nowrap',
    'rounded-full border border-transparent font-medium',
    'transition-[background-color,border-color,box-shadow,color,transform] duration-150 ease-wend',
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
    'disabled:pointer-events-none disabled:opacity-55',
    'aria-busy:pointer-events-none',
    focusRing,
  ),
  {
    variants: {
      variant: {
        /* The brand gradient is reserved for the primary action on a screen. */
        primary: cn(
          'bg-brand-gradient text-white shadow-brand',
          'hover:shadow-brand-lg motion-safe:hover:-translate-y-px',
        ),
        secondary: cn(
          'border-line-strong bg-white/[0.04] text-text',
          'hover:border-white/25 hover:bg-white/[0.08]',
        ),
        ghost: 'text-muted hover:bg-white/[0.06] hover:text-text',
      },
      size: {
        sm: 'h-8 px-3.5 text-[0.82rem]',
        md: 'h-10 px-5 text-[0.92rem]',
        lg: 'h-12 px-6 text-[1rem]',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Shows a spinner, marks the button busy and blocks further clicks. */
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    fullWidth,
    loading = false,
    leadingIcon,
    trailingIcon,
    disabled,
    children,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      // `type` is deliberately not defaulted: a Button keeps native semantics,
      // so one inside a <form> submits it. Give secondary buttons in a form an
      // explicit type="button".
      className={cn(buttonVariants({ variant, size, fullWidth }), className)}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size="sm" label={null} /> : leadingIcon}
      {children}
      {!loading && trailingIcon}
    </button>
  );
});
